import { useState, useEffect, useCallback } from "react";
import { demoApi } from "@/api";
import { toast } from "sonner";
import {
  Zap, RotateCcw, Users, Wrench, Cloud, Radio, Clock,
  X, AlertTriangle, CheckCircle, Activity, ChevronRight, Play,
} from "lucide-react";

const ICONS = { Users, Wrench, Cloud, Radio, Clock, Zap };

const SCENARIO_ORDER = [
  "crew_sick_call",
  "aircraft_aog",
  "weather_del",
  "atc_ground_stop",
  "fdp_watch",
  "full_storm",
];

const SCENARIO_COLORS = {
  crew_sick_call:  "border-amber-500/40  bg-amber-500/8  hover:bg-amber-500/15",
  aircraft_aog:    "border-red-500/40    bg-red-500/8    hover:bg-red-500/15",
  weather_del:     "border-blue-500/40   bg-blue-500/8   hover:bg-blue-500/15",
  atc_ground_stop: "border-purple-500/40 bg-purple-500/8 hover:bg-purple-500/15",
  fdp_watch:       "border-orange-500/40 bg-orange-500/8 hover:bg-orange-500/15",
  full_storm:      "border-rose-500/40   bg-rose-500/8   hover:bg-rose-500/15",
};

const ICON_COLORS = {
  crew_sick_call:  "text-amber-400",
  aircraft_aog:    "text-red-400",
  weather_del:     "text-blue-400",
  atc_ground_stop: "text-purple-400",
  fdp_watch:       "text-orange-400",
  full_storm:      "text-rose-400",
};

export default function DemoControls() {
  const [open,        setOpen]        = useState(false);
  const [scenarios,   setScenarios]   = useState([]);
  const [status,      setStatus]      = useState(null);
  const [loadingId,   setLoadingId]   = useState(null);  // scenario being injected
  const [resetting,   setResetting]   = useState(false);
  const [confirmReset,setConfirmReset]= useState(false);

  const loadStatus = useCallback(async () => {
    try {
      const [sc, st] = await Promise.all([demoApi.getScenarios(), demoApi.getStatus()]);
      setScenarios(sc.data || []);
      setStatus(st.data);
    } catch (e) { console.error("Demo status load:", e); }
  }, []);

  useEffect(() => { if (open) loadStatus(); }, [open, loadStatus]);

  function notifyOpsMapRefresh() {
    window.dispatchEvent(new CustomEvent("demo-data-changed"));
  }

  async function handleReset() {
    if (!confirmReset) { setConfirmReset(true); return; }
    setResetting(true); setConfirmReset(false);
    try {
      const res = await demoApi.reset();
      toast.success(`Perfect world restored — 0 active disruptions. Ops Map cleared.`);
      await loadStatus();
      notifyOpsMapRefresh();
    } catch (e) {
      toast.error("Reset failed. Check backend logs.");
    } finally { setResetting(false); }
  }

  async function handleInject(id) {
    setLoadingId(id);
    try {
      const res = await demoApi.injectScenario(id);
      toast.success(`"${res.data.label}" injected — ${res.data.disruptions_created} disruptions, ${res.data.irrops_created} IRROPS events created.`);
      await loadStatus();
      notifyOpsMapRefresh();
    } catch (e) {
      toast.error(`Failed to inject scenario "${id}"`);
    } finally { setLoadingId(null); }
  }

  // Sorted scenario list matching our order
  const sortedScenarios = SCENARIO_ORDER
    .map(id => scenarios.find(s => s.id === id))
    .filter(Boolean);

  return (
    <>
      {/* Floating trigger button */}
      <button
        onClick={() => setOpen(true)}
        className="relative flex items-center gap-2 px-3 py-1.5 bg-card border border-border rounded-sm text-xs font-bold text-muted-foreground hover:text-foreground hover:border-primary/50 transition-colors"
        data-testid="demo-controls-trigger"
      >
        <Zap size={12} className="text-amber-400" />
        Demo
        {status?.active_disruptions > 0 && (
          <span className="absolute -top-1 -right-1 w-3.5 h-3.5 rounded-full bg-red-500 text-white text-[9px] flex items-center justify-center font-black">
            {status.active_disruptions}
          </span>
        )}
      </button>

      {/* Overlay */}
      {open && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center">
          <div className="absolute inset-0 bg-background/80 backdrop-blur-sm" onClick={() => { setOpen(false); setConfirmReset(false); }} />

          <div className="relative w-[720px] max-h-[88vh] bg-card border border-border rounded-sm shadow-2xl flex flex-col overflow-hidden" data-testid="demo-controls-panel">

            {/* Header */}
            <div className="flex items-center justify-between px-5 py-4 border-b border-border shrink-0">
              <div>
                <div className="flex items-center gap-2">
                  <Zap size={14} className="text-amber-400" />
                  <span className="text-sm font-black uppercase tracking-widest text-foreground">Demo Control Panel</span>
                </div>
                <p className="text-[11px] text-muted-foreground mt-0.5">
                  Inject scenarios or reset demo data for live demonstrations
                </p>
              </div>
              <div className="flex items-center gap-3">
                {status && (
                  <div className="flex items-center gap-3 text-[11px] text-muted-foreground">
                    <div className="flex items-center gap-1.5">
                      <span className={`w-1.5 h-1.5 rounded-full ${status.active_disruptions > 0 ? "bg-red-500 animate-pulse" : "bg-green-500"}`}/>
                      <span>{status.active_disruptions} disruptions</span>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <span className={`w-1.5 h-1.5 rounded-full ${status.active_irrops > 0 ? "bg-amber-500 animate-pulse" : "bg-green-500"}`}/>
                      <span>{status.active_irrops} IRROPS</span>
                    </div>
                  </div>
                )}
                <button onClick={() => { setOpen(false); setConfirmReset(false); }}
                  className="text-muted-foreground hover:text-foreground transition-colors p-1">
                  <X size={16} />
                </button>
              </div>
            </div>

            <div className="flex-1 overflow-y-auto p-5 space-y-5">

              {/* Reset section */}
              <div className="p-4 border border-border rounded-sm bg-background/40">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <div className="text-xs font-black uppercase tracking-widest text-foreground mb-1">Reset to Clean State</div>
                    <p className="text-[11px] text-muted-foreground leading-relaxed">
                      Clears all disruption events and IRROPS events, then re-seeds with the platform's default demo dataset.
                      Crew, roster, CAMO, and training data are untouched.
                    </p>
                  </div>
                  <button
                    onClick={handleReset}
                    disabled={resetting}
                    className={`shrink-0 flex items-center gap-2 px-4 py-2 rounded-sm text-xs font-bold transition-colors disabled:opacity-50 ${
                      confirmReset
                        ? "bg-red-500 text-white hover:bg-red-600 animate-pulse"
                        : "bg-secondary border border-border text-foreground hover:bg-secondary/70"
                    }`}
                    data-testid="demo-reset-btn"
                  >
                    <RotateCcw size={12} className={resetting ? "animate-spin" : ""} />
                    {resetting ? "Resetting..." : confirmReset ? "Confirm Reset" : "Reset All"}
                  </button>
                </div>
                {confirmReset && (
                  <div className="mt-2 flex items-center gap-1.5 text-[11px] text-red-400">
                    <AlertTriangle size={11} />
                    Click again to confirm — all current disruption events will be cleared.
                    <button onClick={() => setConfirmReset(false)} className="text-muted-foreground hover:text-foreground ml-2">Cancel</button>
                  </div>
                )}
              </div>

              {/* Demo flow guide */}
              <div className="p-3 border border-blue-500/20 bg-blue-500/5 rounded-sm">
                <div className="text-[10px] font-black uppercase tracking-widest text-blue-400 mb-2">Recommended Demo Flow</div>
                <div className="grid grid-cols-3 gap-2">
                  {[
                    { step: "1", label: "Reset", desc: "Start clean" },
                    { step: "2", label: "Inject Crew Sick Call", desc: "Show disruption cascade on Ops Map" },
                    { step: "3", label: "Preview Fix", desc: "Select recovery option on map — watch routes turn green" },
                    { step: "4", label: "Inject Full Storm", desc: "Layer all disruptions for maximum impact" },
                    { step: "5", label: "Time Slider", desc: "Drag +3h to show FDP prediction" },
                    { step: "6", label: "Reset", desc: "Clean up before next audience" },
                  ].map(s => (
                    <div key={s.step} className="flex items-start gap-2">
                      <span className="w-5 h-5 shrink-0 rounded-full bg-blue-500/20 border border-blue-500/30 text-[10px] font-black text-blue-400 flex items-center justify-center">{s.step}</span>
                      <div>
                        <div className="text-[11px] font-bold text-foreground">{s.label}</div>
                        <div className="text-[10px] text-muted-foreground">{s.desc}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Scenario cards */}
              <div>
                <div className="text-[10px] font-black uppercase tracking-widest text-muted-foreground mb-3">Inject Scenario</div>
                <div className="grid grid-cols-2 gap-3">
                  {sortedScenarios.map(s => {
                    const Icon    = ICONS[s.icon] || Zap;
                    const loading = loadingId === s.id;
                    return (
                      <div key={s.id}
                        className={`border rounded-sm p-4 transition-colors ${SCENARIO_COLORS[s.id] || "border-border bg-background/40 hover:bg-secondary/20"}`}
                        data-testid={`demo-scenario-${s.id}`}>
                        <div className="flex items-start justify-between gap-2 mb-2">
                          <div className="flex items-center gap-2">
                            <Icon size={14} className={ICON_COLORS[s.id] || "text-muted-foreground"} />
                            <span className="text-xs font-bold text-foreground">{s.label}</span>
                          </div>
                          <div className="flex gap-1 shrink-0">
                            <span className="text-[10px] text-muted-foreground font-mono">{s.disruption_count}D</span>
                            <span className="text-[10px] text-muted-foreground font-mono">{s.irrops_count}I</span>
                          </div>
                        </div>
                        <p className="text-[11px] text-muted-foreground leading-relaxed mb-3">{s.description}</p>
                        <button
                          onClick={() => handleInject(s.id)}
                          disabled={!!loadingId}
                          className="w-full flex items-center justify-center gap-2 py-1.5 bg-primary/10 border border-primary/30 text-primary rounded-sm text-[11px] font-bold hover:bg-primary/20 transition-colors disabled:opacity-50"
                          data-testid={`demo-inject-${s.id}`}>
                          {loading
                            ? <><RotateCcw size={11} className="animate-spin" /> Injecting...</>
                            : <><Play size={11} /> Inject Scenario</>
                          }
                        </button>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Tips */}
              <div className="grid grid-cols-2 gap-3 text-[11px] text-muted-foreground">
                <div className="flex items-start gap-2 p-3 border border-border/50 rounded-sm">
                  <Activity size={12} className="text-primary shrink-0 mt-0.5" />
                  <div><strong className="text-foreground">After injecting</strong> — go to <strong>Ops Map</strong> and click the event in the left sidebar to see the full cascade chain and recovery options.</div>
                </div>
                <div className="flex items-start gap-2 p-3 border border-border/50 rounded-sm">
                  <Clock size={12} className="text-amber-400 shrink-0 mt-0.5" />
                  <div><strong className="text-foreground">FDP Watch</strong> — after injecting FDP scenario, use the <strong>time slider</strong> at the bottom-right of the Ops Map (+3h) to show route colour changes.</div>
                </div>
              </div>

            </div>
          </div>
        </div>
      )}
    </>
  );
}
